import type { DatePeriod } from '@/utils/date';
import {
  parseBooleanFilter,
  parseNumberFilter,
  parseNumberListFilter,
  parseTextFilter,
  toBooleanFilterText,
  toFilterText,
  toGeoFilterValues,
  toGeoSearch,
  toNumberListFilterText,
  toPeriodFilterValues,
  toPeriodSearch,
} from '@/utils/filters';
import type {
  MpApprovalSearch,
  MpRegionFilterSearch,
  MpReportSearch,
} from '../types/marketPromotion.types';
import type {
  DonationBatchSearch,
  DonationCandidateSearch,
  DonationRequisitionSearch,
} from '../types/marketPromotionP2.types';

type FilterValues = Record<string, string>;

type DonationApprovalFilterSearch = Pick<
  MpApprovalSearch,
  | 'dateFrom'
  | 'dateTo'
  | 'enteredBy'
  | 'promoItemIds'
  | 'approveTypeNos'
  | 'callStatus'
  | 'teacherFeedback'
  | 'calledBy'
  | 'callDateFrom'
  | 'callDateTo'
  | 'isMisVerified'
>;

function toPeriodValues(
  search: DonationApprovalFilterSearch,
  defaultPeriod: DatePeriod | undefined,
): FilterValues {
  if (defaultPeriod) return toPeriodFilterValues(search, defaultPeriod);
  return { dateFrom: toFilterText(search.dateFrom), dateTo: toFilterText(search.dateTo) };
}

export function toDonationApprovalFilterValues(
  search: DonationApprovalFilterSearch,
  defaultPeriod: DatePeriod | undefined,
): FilterValues {
  return {
    ...toPeriodValues(search, defaultPeriod),
    enteredBy: toFilterText(search.enteredBy),
    approveTypeNos: toNumberListFilterText(search.approveTypeNos),
    promoItemIds: toNumberListFilterText(search.promoItemIds),
    callStatus: toFilterText(search.callStatus),
    teacherFeedback: toFilterText(search.teacherFeedback),
    calledBy: toFilterText(search.calledBy),
    isMisVerified: toBooleanFilterText(search.isMisVerified),
    callDateFrom: toFilterText(search.callDateFrom),
    callDateTo: toFilterText(search.callDateTo),
  };
}

type DonationBatchFilterSearch = Pick<DonationBatchSearch, 'dateFrom' | 'dateTo' | 'userId'>;

export function toDonationBatchFilterValues(search: DonationBatchFilterSearch): FilterValues {
  return {
    dateFrom: toFilterText(search.dateFrom),
    dateTo: toFilterText(search.dateTo),
    userId: toFilterText(search.userId),
  };
}

export function toDonationBatchFilterSearch(values: FilterValues): DonationBatchFilterSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
  };
}

type DonationCandidateFilterSearch = Omit<DonationCandidateSearch, 'q'>;

export function toDonationCandidateFilterValues(
  search: DonationCandidateFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    promoItemId: toFilterText(search.promoItemId),
    ...toPeriodFilterValues(search, defaultPeriod),
  };
}

export function toDonationCandidateFilterSearch(
  values: FilterValues,
): DonationCandidateFilterSearch {
  return {
    promoItemId: parseNumberFilter(values.promoItemId),
    ...toPeriodSearch(values),
  };
}

export type MpReportFilterSearch = Pick<
  MpReportSearch,
  'dateFrom' | 'dateTo' | 'userId' | 'divisionId' | 'districtId' | 'thanaId'
>;

export type MpSummaryFilterSearch = Pick<
  MpReportSearch,
  'dateFrom' | 'dateTo' | 'userId' | 'promoItemId'
>;

export function toMpReportFilterValues(
  search: MpReportFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    ...toPeriodFilterValues(search, defaultPeriod),
    userId: toFilterText(search.userId),
    ...toGeoFilterValues(search),
  };
}

export function toMpReportFilterSearch(values: FilterValues): MpReportFilterSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    ...toGeoSearch(values),
  };
}

export function toMpSummaryFilterValues(
  search: MpSummaryFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    ...toPeriodFilterValues(search, defaultPeriod),
    userId: toFilterText(search.userId),
    promoItemId: toFilterText(search.promoItemId),
  };
}

export function toMpSummaryFilterSearch(values: FilterValues): MpSummaryFilterSearch {
  return {
    ...toPeriodSearch(values),
    userId: parseNumberFilter(values.userId),
    promoItemId: parseNumberFilter(values.promoItemId),
  };
}

export function toMpRegionFilterValues(
  search: MpRegionFilterSearch,
  defaultPeriod: DatePeriod,
): FilterValues {
  return {
    ...toPeriodFilterValues(search, defaultPeriod),
    budgetHolderId: toFilterText(search.budgetHolderId),
    ...toGeoFilterValues(search),
    promoItemIds: toNumberListFilterText(search.promoItemIds),
    itemCode: toFilterText(search.itemCode),
  };
}

export function toMpRegionFilterSearch(values: FilterValues): MpRegionFilterSearch {
  return {
    ...toPeriodSearch(values),
    budgetHolderId: parseNumberFilter(values.budgetHolderId),
    ...toGeoSearch(values),
    promoItemIds: parseNumberListFilter(values.promoItemIds),
    itemCode: parseTextFilter(values.itemCode),
  };
}

type DonationRequisitionFilterSearch = Pick<
  DonationRequisitionSearch,
  'dateFrom' | 'dateTo' | 'instituteTypeNo'
>;

export function toDonationRequisitionFilterValues(
  search: DonationRequisitionFilterSearch,
): FilterValues {
  return {
    dateFrom: toFilterText(search.dateFrom),
    dateTo: toFilterText(search.dateTo),
    instituteTypeNo: toFilterText(search.instituteTypeNo),
  };
}

export function toDonationRequisitionFilterSearch(
  values: FilterValues,
): DonationRequisitionFilterSearch {
  return {
    ...toPeriodSearch(values),
    instituteTypeNo: parseNumberFilter(values.instituteTypeNo),
  };
}

export function toDonationApprovalFilterSearch(values: FilterValues): DonationApprovalFilterSearch {
  const isMisVerified = parseBooleanFilter(values.isMisVerified);
  return {
    ...toPeriodSearch(values),
    enteredBy: parseNumberFilter(values.enteredBy),
    approveTypeNos: parseNumberListFilter(values.approveTypeNos),
    promoItemIds: parseNumberListFilter(values.promoItemIds),
    callStatus: parseNumberFilter(values.callStatus),
    teacherFeedback: parseNumberFilter(values.teacherFeedback),
    calledBy: parseNumberFilter(values.calledBy),
    isMisVerified,
    callDateFrom: isMisVerified === true ? parseTextFilter(values.callDateFrom) : undefined,
    callDateTo: isMisVerified === true ? parseTextFilter(values.callDateTo) : undefined,
  };
}
